/**
 * A `TimePicker` bound to a `react-hook-form` field.
 *
 * The same reason `SelectField` exists: `TimePicker` is not a form-native
 * control — there is no `<input name>` in its output — so `{...register('at')}`
 * compiles, renders, *displays a value* and then reports "required" on submit.
 * That failure has already cost a round in this project on the Radix select.
 * `Controller` is the binding, and wrapping it here keeps the call sites from
 * each carrying the same ten lines.
 *
 * The label, hint and error still come from `Field`; this replaces only the
 * control.
 */
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form'

import { TimePicker, type TimePickerLabels } from './TimePicker'

export type TimePickerFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  labels: TimePickerLabels
  format: (value: string) => string
  clearable?: boolean | undefined
  disabled?: boolean | undefined
  minuteStep?: number | undefined
  className?: string | undefined
}

export function TimePickerField<T extends FieldValues>({
  control,
  name,
  labels,
  format,
  clearable,
  disabled,
  minuteStep,
  className,
}: TimePickerFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <TimePicker
          // `?? ''` — an undefined value would leave the trigger rendering
          // "undefined" and stop it reflecting `form.reset()`, which is how an
          // edit screen loads a stored time.
          value={String(field.value ?? '')}
          onChange={field.onChange}
          onBlur={field.onBlur}
          labels={labels}
          format={format}
          name={field.name}
          {...(clearable ? { clearable } : {})}
          {...((disabled ?? field.disabled) ? { disabled: true } : {})}
          {...(minuteStep === undefined ? {} : { minuteStep })}
          {...(className ? { className } : {})}
        />
      )}
    />
  )
}
